import { Eye, EyeOff } from 'lucide-react';
import { MASKED_MONEY, formatMoney } from '@thrivehrm/shared';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

/**
 * Eye / eye-off toggle for masked values (DESIGN_SYSTEM §4): a ghost icon button (primary icon,
 * primary-soft hover). `variant="header"` is the labelled
 * "Show salaries" button for salary-heavy pages. Pair with `useSalaryReveal()` so every toggle
 * on the page shares one per-session state.
 */
function RevealToggle({
  revealed,
  onRevealedChange,
  variant = 'icon',
  subject = 'salaries',
  disabled,
  className,
}: {
  revealed: boolean;
  onRevealedChange: (revealed: boolean) => void;
  variant?: 'icon' | 'header';
  /** What gets revealed, used in labels: "Show salaries". */
  subject?: string;
  disabled?: boolean;
  className?: string;
}) {
  const label = `${revealed ? 'Hide' : 'Show'} ${subject}`;
  const Icon = revealed ? EyeOff : Eye;
  const toggle = () => {
    onRevealedChange(!revealed);
  };

  if (variant === 'header') {
    return (
      <Button
        variant="secondary"
        aria-pressed={revealed}
        disabled={disabled}
        onClick={toggle}
        {...(className ? { className } : {})}
      >
        <Icon aria-hidden="true" />
        {label}
      </Button>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={revealed}
          disabled={disabled}
          onClick={toggle}
          {...(className ? { className } : {})}
        >
          <Icon aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** A money amount that is masked (`৳ ••••••`, muted) until revealed. */
function MoneyValue({
  amount,
  revealed,
  className,
}: {
  amount: number | string | null | undefined;
  revealed: boolean;
  className?: string;
}) {
  if (!revealed) {
    return (
      <span className={cn('tabular text-muted-foreground', className)}>
        <span aria-hidden="true">{MASKED_MONEY}</span>
        <span className="sr-only">Hidden</span>
      </span>
    );
  }
  const text = formatMoney(amount);
  return (
    <span
      className={cn(
        'tabular',
        (amount === null || amount === undefined || amount === '') && 'text-subtle-foreground',
        className,
      )}
    >
      {text}
    </span>
  );
}

export { MoneyValue, RevealToggle };
